import { create } from "zustand";
import type {
  InstantSearchConfig,
  InstantSearchResult,
} from "../types/instantsearch-types";

// ✅ Facet değeri için tip tanımı
interface FacetValue {
  value: string; // Display value (örn: "Siyah", "M")
  valueId: string; // attributeValueId (backend'e gönderilen)
  count: number; // Ürün sayısı
  attrId: string; // Attribute ID (gruplama için)
  attrName: string; // Attribute name (örn: "Renk", "Beden")
  isVariantable?: boolean; // 🆕 UI'da önceliklendirme + primary axis yönetimi için
}

export interface InstantSearchSuggestion {
  type: "category" | "brand" | "merchant";
  value: string; // Display name
  valueId?: string; // Filter ID (yoksa value kullanılır)
  count: number;
  filterKey: string;
  displayFilterKey?: string;
}

interface InstantSearchState<T = any> {
  isOpen: boolean;
  config: InstantSearchConfig<T> | null;
  query: string;
  categoryId: string | undefined; // 🆕 Kategori ID - Facet builder için gerekli
  facetFilters: Record<string, string[]>; // 🆕 Format: { attributeId: [valueId1, valueId2] }
  primaryVariantAxis: string | null; // 🆕 İlk seçilen variant attribute ID'si
  cachedVariantFacets: Record<string, FacetValue[]>; // 🆕 Cache'lenmiş variant facet'ler
  results: InstantSearchResult[];
  availableFacets: Record<string, FacetValue[]>; // ✅ Updated: valueId eklendi
  suggestions: InstantSearchSuggestion[]; // 🆕 Autocomplete suggestions (store'da tutulur)
  isLoading: boolean;
  open: (config: InstantSearchConfig<T>, categoryId?: string) => void; // 🆕 categoryId parametresi
  close: () => void;
  setQuery: (query: string) => void; // 🔍 Query state (URL + input senkron)
  setCategoryId: (categoryId: string | undefined) => void; // 🆕 Category ID setter
  setFacetFilters: (field: string, values: string[]) => void;
  clearFacetFilters: () => void;
  setPrimaryVariantAxis: (attrId: string | null) => void; // 🆕 Primary axis setter
  setCachedVariantFacets: (facets: Record<string, FacetValue[]>) => void; // 🆕 Cache'lenmiş variant facet'ler setter
  setResults: (results: InstantSearchResult[]) => void;
  setAvailableFacets: (facets: Record<string, FacetValue[]>) => void; // ✅ Updated
  setSuggestions: (suggestions: InstantSearchSuggestion[]) => void; // 🆕
  setLoading: (loading: boolean) => void;
  reset: () => void;
}

export const useInstantSearch = create<InstantSearchState>((set) => ({
  isOpen: false,
  config: null,
  query: "",
  categoryId: undefined, // 🆕 Default: undefined (tüm attribute'lar)
  facetFilters: {},
  primaryVariantAxis: null, // 🆕 İlk seçilen variant attribute ID'si
  cachedVariantFacets: {}, // 🆕 Cache'lenmiş variant facet'ler
  results: [],
  availableFacets: {},
  suggestions: [],
  isLoading: false,
  open: (config, categoryId) =>
    set({
      isOpen: true,
      config, // Config'i set et
      categoryId, // 🆕 Category ID'yi store'a kaydet
      // ⚠️ ÖNEMLİ: Burada query'yi SIFIRLAMA!
      // Query artık URL'den veya UI'dan senkronize ediliyor.
      // open çağrısı sadece config + categoryId yönetmeli.
      facetFilters: {}, // Facet filtrelerini sıfırla
      primaryVariantAxis: null, // 🆕 Primary axis'i sıfırla
      cachedVariantFacets: {}, // 🆕 Cache'lenmiş variant facet'leri temizle
      results: [], // Sonuçları temizle
      availableFacets: {}, // Facetleri temizle
      suggestions: [], // 🆕 Suggestions'ı temizle
    }),
  close: () =>
    set({
      isOpen: false,
      config: null,
      categoryId: undefined, // 🆕 Reset category ID
      query: "",
      facetFilters: {},
      primaryVariantAxis: null, // 🆕 Reset primary axis
      cachedVariantFacets: {}, // 🆕 Reset cached variant facets
      results: [],
      availableFacets: {},
      suggestions: [],
    }),
  setQuery: (query) => set({ query }),
  setCategoryId: (categoryId) => set({ categoryId }), // 🆕 Category ID setter
  setFacetFilters: (field, values) =>
    set((state) => ({
      facetFilters: { ...state.facetFilters, [field]: values },
    })),
  clearFacetFilters: () =>
    set({
      facetFilters: {},
      primaryVariantAxis: null,
      cachedVariantFacets: {},
    }),
  setPrimaryVariantAxis: (attrId) => set({ primaryVariantAxis: attrId }), // 🆕 Primary axis setter
  setCachedVariantFacets: (facets) => set({ cachedVariantFacets: facets }), // 🆕 Cached variant facets setter
  setResults: (results) => set({ results }),
  setAvailableFacets: (facets) => set({ availableFacets: facets }),
  setSuggestions: (suggestions) => set({ suggestions }),
  setLoading: (isLoading) => set({ isLoading }),
  reset: () =>
    set({
      query: "",
      categoryId: undefined, // 🆕 Reset category ID
      facetFilters: {},
      primaryVariantAxis: null, // 🆕 Reset primary axis
      cachedVariantFacets: {}, // 🆕 Reset cached variant facets
      results: [],
      availableFacets: {},
      suggestions: [],
      isLoading: false,
    }),
}));
